import React, { useState } from "react";
import { PrivateOfficeUpdateRequest } from "@/services/private-office-service/types";
import {
  isImageRequest,
  isImageDeletionRequest,
  IMAGE_PROPERTIES,
  getPreviousImageUrl,
  getImageDeletionProperty,
  IMAGE_PROPERTY_NAMES,
} from "@/utils/image-utils";
import {
  Check,
  Trash2,
  User,
  Calendar,
  CalendarCheck,
  ImageOff,
} from "lucide-react";

// Função segura para obter a URL da nova imagem (retorna null em vez de lançar erro)
const getNewImageUrlSafe = (
  fieldsToUpdate: Record<string, unknown>,
): string | null => {
  for (const property of IMAGE_PROPERTIES) {
    if (fieldsToUpdate[property]) {
      return fieldsToUpdate[property] as string;
    }
  }
  return null;
};

interface ApprovedImageRequestProps {
  request: PrivateOfficeUpdateRequest;
}

export const ApprovedImageRequest: React.FC<ApprovedImageRequestProps> = ({
  request,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);

  if (!isImageRequest(request.fields_to_update)) {
    return null;
  }

  const isDeletion = isImageDeletionRequest(request.fields_to_update);
  const deletionProperty = isDeletion
    ? getImageDeletionProperty(request.fields_to_update)
    : null;
  const propertyName = deletionProperty
    ? IMAGE_PROPERTY_NAMES[deletionProperty]
    : "Imagem";

  // Se foi deleção, mostrar imagem anterior; senão, mostrar nova imagem
  const imageUrl = isDeletion
    ? getPreviousImageUrl(request.previous_values)
    : getNewImageUrlSafe(request.fields_to_update);

  return (
    <div
      className={`bg-white rounded-lg border p-6 space-y-6 ${
        isDeletion ? "border-orange-200" : "border-green-200"
      }`}
    >
      <div
        className={`border-b pb-4 ${
          isDeletion ? "border-orange-200" : "border-green-200"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDeletion ? "bg-orange-100" : "bg-green-100"
            }`}
          >
            {isDeletion ? (
              <Trash2 className="w-5 h-5 text-orange-600" />
            ) : (
              <Check className="w-5 h-5 text-green-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isDeletion
                ? `Deleção de ${propertyName} Aprovada`
                : "Solicitação de Alteração de Imagem"}{" "}
              - {request.office_info.name}
            </h3>
            <p className="text-sm text-gray-600">
              Solicitado por {request.requested_by.username} em{" "}
              {new Date(request.created_at).toLocaleDateString("pt-BR")}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Alerta de aprovação */}
        <div
          className={`border rounded-lg p-4 ${
            isDeletion
              ? "bg-orange-50 border-orange-200"
              : "bg-green-50 border-green-200"
          }`}
        >
          <div className="flex items-start gap-3">
            {isDeletion ? (
              <Trash2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            )}
            <div>
              <h4
                className={`font-medium ${
                  isDeletion ? "text-orange-800" : "text-green-800"
                }`}
              >
                {isDeletion ? "Deleção Aprovada" : "Solicitação Aprovada"}
              </h4>
              <p
                className={`text-sm mt-1 ${
                  isDeletion ? "text-orange-700" : "text-green-700"
                }`}
              >
                {isDeletion
                  ? `A ${propertyName.toLowerCase()} foi removida permanentemente da sala.`
                  : "Esta imagem foi aprovada e já está sendo exibida na sala."}
              </p>
            </div>
          </div>
        </div>

        {/* Imagem */}
        {imageUrl ? (
          <div className="text-center">
            <h4 className="font-medium text-gray-900 mb-4">
              {isDeletion ? "Imagem Deletada" : "Imagem Aprovada"}
            </h4>
            <div className="relative max-w-sm mx-auto">
              <div
                className={`group aspect-video rounded-xl overflow-hidden shadow-lg ring-2 cursor-pointer transition-all duration-200 relative ${
                  isDeletion
                    ? "ring-orange-200 hover:ring-orange-300"
                    : "ring-green-200 hover:ring-green-300"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(true);
                }}
              >
                <img
                  src={imageUrl}
                  alt={isDeletion ? "Imagem deletada" : "Imagem aprovada"}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                />
                {!isDeletion && request.additional_data?.tags && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b text-center">
                    {request.additional_data?.tags?.join(", ")}
                  </div>
                )}
                {/* Overlay */}
                <div
                  className={`absolute inset-0 bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
                    isDeletion ? "bg-orange-500" : "bg-green-500"
                  }`}
                >
                  <div
                    className={`text-white px-3 py-1 rounded-full text-sm font-medium ${
                      isDeletion ? "bg-orange-600" : "bg-green-600"
                    }`}
                  >
                    {isDeletion ? "Deletada" : "Aprovada"}
                  </div>
                </div>
              </div>
              <div
                className={`absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center shadow-lg ${
                  isDeletion ? "bg-orange-500" : "bg-green-500"
                }`}
              >
                {isDeletion ? (
                  <Trash2 className="w-3 h-3 text-white" />
                ) : (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <div className="text-center mt-3">
                <p className="text-sm text-gray-600 font-medium">
                  {isDeletion
                    ? "Imagem que foi deletada (clique para ampliar)"
                    : "Imagem aprovada (clique para ampliar)"}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageOff className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-600">
              {isDeletion
                ? "A imagem deletada não está disponível para visualização."
                : "A imagem aprovada não está disponível."}
            </p>
          </div>
        )}
      </div>

      {/* Detalhes */}
      <div className="pt-4 border-t border-gray-200">
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <User className="w-4 h-4" />
            <span>
              Solicitado por:{" "}
              <span className="font-medium">
                {request.requested_by.username}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>
              Data:{" "}
              <span className="font-medium">
                {new Date(request.created_at).toLocaleDateString("pt-BR")}
              </span>
            </span>
          </div>
          {request.reviewed_at && request.reviewed_by && (
            <div className="flex items-center gap-2 text-gray-600">
              <CalendarCheck className="w-4 h-4" />
              <span>
                Aprovada em:{" "}
                <span className="font-medium">
                  {new Date(request.reviewed_at).toLocaleDateString("pt-BR")}
                  {` por ${request.reviewed_by.name}`}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Modal para visualizar imagem ampliada */}
      {showImageModal && imageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={imageUrl}
              alt={isDeletion ? "Imagem deletada" : "Imagem aprovada"}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};
